import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft,
  Users,
  Plus,
  Shield,
  UserMinus,
  UserCog,
  Shirt,
  Search,
  X,
  Loader2,
} from "lucide-react";

export default function TeamDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [addTab, setAddTab] = useState("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [anonName, setAnonName] = useState("");
  const [adding, setAdding] = useState(false);
  const [savingMember, setSavingMember] = useState(null); // {memberId, field} for inline save

  useEffect(() => {
    fetchTeam();
  }, [id]);

  useEffect(() => {
    if (!searchQuery.trim() || addTab !== "search") {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get(`/players/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(r.data?.players || r.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, addTab]);

  const fetchTeam = async () => {
    try {
      const r = await api.get(`/teams/${id}`);
      setTeam(r.data.team);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setTeam(null);
    } finally {
      setLoading(false);
    }
  };

  const isCreator = user && team && team.creator_id === user.id;

  const handleRemoveMember = async (memberId) => {
    if (!confirm(t("team.remove_confirm"))) return;
    try {
      await api.delete(`/teams/${id}/members/${memberId}`);
      toast.success(t("team.member_removed"));
      fetchTeam();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleLeaveTeam = async () => {
    if (!confirm(t("team.leave_confirm"))) return;
    try {
      await api.delete(`/teams/${id}/members/me`);
      toast.success(t("team.left_team"));
      fetchTeam();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleUpdateRole = async (memberId, role) => {
    setSavingMember({ memberId, field: "role" });
    try {
      await api.patch(`/teams/${id}/members/${memberId}`, { role });
      toast.success(t("team.member_updated"));
      fetchTeam();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingMember(null);
    }
  };

  const handleUpdateJersey = async (memberId, jerseyNumber) => {
    setSavingMember({ memberId, field: "jersey" });
    try {
      await api.patch(`/teams/${id}/members/${memberId}`, { jersey_number: parseInt(jerseyNumber) || null });
      toast.success(t("team.member_updated"));
      fetchTeam();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingMember(null);
    }
  };

  const handleAddRegistered = async (player) => {
    setAdding(true);
    try {
      await api.post(`/teams/${id}/members`, {
        member_type: "registered",
        player_id: player.id,
        name: player.name || player.display_name || player.email,
      });
      toast.success(t("team.member_added"));
      setShowAddMember(false);
      setSearchQuery("");
      setSearchResults([]);
      fetchTeam();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const handleAddAnonymous = async () => {
    if (!anonName.trim()) return;
    setAdding(true);
    try {
      await api.post(`/teams/${id}/members`, {
        member_type: "anonymous",
        name: anonName.trim(),
      });
      toast.success(t("team.member_added"));
      setShowAddMember(false);
      setAnonName("");
      fetchTeam();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const roleBadge = (role) => {
    const styles = {
      captain: "bg-volt/10 text-volt",
      player: "bg-blue-500/10 text-blue-400",
      substitute: "bg-muted text-muted-foreground",
    };
    return (
      <span className={`text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-1 ${styles[role] || styles.player}`}>
        {t("team.role_" + role)}
      </span>
    );
  };

  const memberTypeLabel = (member) => {
    if (member.member_type === "anonymous") {
      return <span className="text-[10px] text-muted-foreground">{t("team.type_anonymous")}</span>;
    }
    return <span className="text-[10px] text-volt">{t("team.type_registered")}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-volt border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center">
        <p className="text-muted-foreground">{t("team.not_found")}</p>
      </div>
    );
  }

  const members = team.roster || [];
  const myMember = user && members.find((m) => m.user_id === user.id);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background max-w-[900px] mx-auto px-5 md:px-10 py-10">
      {/* Back button */}
      <button
        onClick={() => navigate("/teams")}
        className="btn-outline px-4 py-2 text-xs tracking-widest uppercase font-semibold flex items-center gap-2 mb-6"
      >
        <ArrowLeft className="h-3 w-3" /> {t("team.back")}
      </button>

      {/* Team info header */}
      <div className="bg-card border border-border p-6 md:p-8 mb-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              {team.logo ? (
                <div className="h-12 w-12 rounded-full overflow-hidden border border-border flex-shrink-0">
                  <img src={team.logo} alt={team.name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="h-10 w-10 rounded-full bg-volt/10 flex items-center justify-center flex-shrink-0">
                  <Users className="h-5 w-5 text-volt" />
                </div>
              )}
            </div>
            <h1 className="font-heading text-3xl md:text-5xl tracking-tight mb-3">{team.name}</h1>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Shirt className="h-4 w-4" /> {t("home.sports." + (team.sport || "other"))}
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-4 w-4" /> {members.length} {t("team.members")}
              </span>
              <span>{t("team.created")}: {team.created_at?.slice(0, 10)}</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {isCreator && (
              <button
                onClick={() => setShowAddMember(true)}
                className="btn-volt px-6 py-3 text-xs tracking-widest uppercase font-bold flex items-center gap-2"
              >
                <Plus className="h-4 w-4" /> {t("team.add_member")}
              </button>
            )}
            {!isCreator && myMember && (
              <button
                onClick={handleLeaveTeam}
                className="btn-outline px-6 py-3 text-xs tracking-widest uppercase font-bold"
              >
                {t("team.leave_team")}
              </button>
            )}
          </div>
        </div>

        {team.description && (
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground">{team.description}</p>
          </div>
        )}
      </div>

      {/* Add Member modal/section */}
      {showAddMember && isCreator && (
        <div className="bg-card border border-border p-6 md:p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl tracking-tight">{t("team.add_member")}</h2>
            <button
              onClick={() => {
                setShowAddMember(false);
                setSearchQuery("");
                setSearchResults([]);
                setAnonName("");
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 border-b border-border mb-6">
            <button
              onClick={() => setAddTab("search")}
              className={`pb-3 text-xs tracking-widest uppercase font-bold border-b-2 transition-colors ${
                addTab === "search"
                  ? "border-volt text-volt"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Search className="h-3 w-3 inline mr-1.5" />
              {t("team.search_player")}
            </button>
            <button
              onClick={() => setAddTab("anonymous")}
              className={`pb-3 text-xs tracking-widest uppercase font-bold border-b-2 transition-colors ${
                addTab === "anonymous"
                  ? "border-volt text-volt"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <UserCog className="h-3 w-3 inline mr-1.5" />
              {t("team.add_anonymous")}
            </button>
          </div>

          {/* Search tab */}
          {addTab === "search" && (
            <div>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("team.search_player_placeholder")}
                  className="w-full bg-background border border-border pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-volt"
                />
              </div>
              {searching && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
              {!searching && searchResults.length === 0 && searchQuery.trim() && (
                <p className="text-sm text-muted-foreground text-center py-8">{t("team.no_results")}</p>
              )}
              {!searching && searchResults.length > 0 && (
                <div className="space-y-2">
                  {searchResults.map((player) => (
                    <div
                      key={player.id}
                      className="bg-background border border-border px-4 py-3 flex items-center justify-between hover:border-volt transition-colors cursor-pointer"
                      onClick={() => !adding && handleAddRegistered(player)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-volt/10 text-volt flex items-center justify-center font-bold text-xs">
                          {(player.name || player.display_name || player.email || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-xs font-semibold">{player.name || player.display_name || player.email}</p>
                          {player.email && (player.name || player.display_name) && (
                            <p className="text-[10px] text-muted-foreground">{player.email}</p>
                          )}
                        </div>
                      </div>
                      <Plus className="h-4 w-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              )}
              {!searchQuery.trim() && !searching && (
                <p className="text-sm text-muted-foreground text-center py-8">{t("team.search_hint")}</p>
              )}
            </div>
          )}

          {/* Anonymous tab */}
          {addTab === "anonymous" && (
            <div className="flex gap-3">
              <input
                type="text"
                value={anonName}
                onChange={(e) => setAnonName(e.target.value)}
                placeholder={t("team.anonymous_name_placeholder")}
                className="flex-1 bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-volt"
                onKeyDown={(e) => e.key === "Enter" && !adding && handleAddAnonymous()}
              />
              <button
                onClick={handleAddAnonymous}
                disabled={adding || !anonName.trim()}
                className="btn-volt px-6 py-3 text-xs tracking-widest uppercase font-bold flex items-center gap-2"
              >
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {t("team.add")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Roster list */}
      <div className="bg-card border border-border p-6 md:p-8">
        <h2 className="font-heading text-xl tracking-tight mb-6">
          {t("team.roster")} ({members.length})
        </h2>
        {members.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("team.no_members")}</p>
        ) : (
          <div className="space-y-3">
            {members.map((member) => (
              <div
                key={member._id}
                className="bg-background border border-border px-4 py-3 flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-8 w-8 bg-volt/10 text-volt flex items-center justify-center font-bold text-xs shrink-0">
                    {(member.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs font-semibold truncate">{member.name}</p>
                      {roleBadge(member.role)}
                      {memberTypeLabel(member)}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                      {member.jersey_number != null && (
                        <span className="flex items-center gap-1">
                          <Shirt className="h-3 w-3" /> #{member.jersey_number}
                        </span>
                      )}
                      <span>{t("team.member_since")}: {member.joined_at?.slice(0, 10) || member.created_at?.slice(0, 10)}</span>
                    </div>
                  </div>
                </div>

                {/* Creator management controls */}
                {isCreator && member.user_id !== user.id && (
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Role dropdown */}
                    <select
                      value={member.role || "player"}
                      onChange={(e) => handleUpdateRole(member._id, e.target.value)}
                      disabled={savingMember?.memberId === member._id && savingMember?.field === "role"}
                      className="bg-background border border-border text-[10px] px-2 py-1.5 focus:outline-none focus:border-volt"
                    >
                      <option value="captain">{t("team.role_captain")}</option>
                      <option value="player">{t("team.role_player")}</option>
                      <option value="substitute">{t("team.role_substitute")}</option>
                    </select>

                    {/* Jersey number input */}
                    <input
                      type="number"
                      min="0"
                      max="99"
                      defaultValue={member.jersey_number ?? ""}
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val !== "" || member.jersey_number != null) {
                          handleUpdateJersey(member._id, val || null);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.target.blur();
                        }
                      }}
                      placeholder="#"
                      className="w-12 bg-background border border-border text-[10px] text-center px-1 py-1.5 focus:outline-none focus:border-volt"
                    />

                    <button
                      onClick={() => handleRemoveMember(member._id)}
                      className="text-red-400 hover:text-red-300 p-1.5"
                      title={t("team.remove_member")}
                    >
                      <UserMinus className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
